import { useCallback, useEffect, useRef } from 'react'
import { type RecognitionErrorKind } from './voiceControl.types'

interface SpeechRecognitionResultEvent {
  resultIndex: number
  results: ArrayLike<ArrayLike<{ transcript: string }>>
}

interface SpeechRecognitionErrorEvent {
  error: string
}

export interface SpeechRecognitionLike {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

interface SpeechRecognitionWindow {
  SpeechRecognition?: SpeechRecognitionCtor
  webkitSpeechRecognition?: SpeechRecognitionCtor
}

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as SpeechRecognitionWindow
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

function toErrorKind(error: string): RecognitionErrorKind {
  if (
    error === 'not-allowed' ||
    error === 'no-speech' ||
    error === 'audio-capture' ||
    error === 'network'
  ) {
    return error
  }
  return 'unknown'
}

interface Options {
  lang: string
  onTranscript: (transcript: string) => void
  onError?: (error: RecognitionErrorKind) => void
}

const RESTART_DELAY_MS = 350
const RAPID_FAIL_MS = 800
const MAX_RAPID_FAILS = 6

export function useSpeechRecognition({ lang, onTranscript, onError }: Options) {
  const Ctor = getRecognitionCtor()
  const isSupported = Boolean(Ctor)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const activeRef = useRef(false)
  const streamRef = useRef<MediaStream | null>(null)
  const restartTimer = useRef<ReturnType<typeof setTimeout>>()
  const lastStartAt = useRef(0)
  const rapidFails = useRef(0)
  const optsRef = useRef({ onTranscript, onError })
  optsRef.current = { onTranscript, onError }

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop())
    streamRef.current = null
  }, [])

  const stop = useCallback(() => {
    activeRef.current = false
    clearTimeout(restartTimer.current)
    recognitionRef.current?.stop()
    releaseStream()
  }, [releaseStream])

  const beginSession = useCallback(() => {
    if (!Ctor || !activeRef.current) return
    if (!recognitionRef.current) {
      const recognition = new Ctor()
      recognition.continuous = false
      recognition.interimResults = false
      recognition.lang = lang
      recognition.onresult = event => {
        rapidFails.current = 0
        for (
          let index = event.resultIndex;
          index < event.results.length;
          index++
        ) {
          optsRef.current.onTranscript(event.results[index][0].transcript)
        }
      }
      recognition.onerror = event => {
        const kind = toErrorKind(event.error)
        if (kind === 'not-allowed' || kind === 'audio-capture') {
          activeRef.current = false
        }
        optsRef.current.onError?.(kind)
      }
      recognition.onend = () => {
        if (!activeRef.current) return
        const elapsed = Date.now() - lastStartAt.current
        rapidFails.current =
          elapsed < RAPID_FAIL_MS ? rapidFails.current + 1 : 0
        if (rapidFails.current >= MAX_RAPID_FAILS) {
          activeRef.current = false
          releaseStream()
          optsRef.current.onError?.('network')
          return
        }
        restartTimer.current = setTimeout(() => {
          if (!activeRef.current) return
          lastStartAt.current = Date.now()
          try {
            recognitionRef.current?.start()
          } catch {
            return
          }
        }, RESTART_DELAY_MS)
      }
      recognitionRef.current = recognition
    }
    recognitionRef.current.lang = lang
    lastStartAt.current = Date.now()
    try {
      recognitionRef.current.start()
    } catch {
      return
    }
  }, [Ctor, lang, releaseStream])

  const start = useCallback(async () => {
    if (!Ctor) return
    activeRef.current = true
    rapidFails.current = 0
    if (!streamRef.current && navigator.mediaDevices?.getUserMedia) {
      try {
        streamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: true
        })
      } catch {
        return
      }
    }
    if (!activeRef.current) {
      releaseStream()
      return
    }
    beginSession()
  }, [Ctor, beginSession, releaseStream])

  useEffect(() => {
    return () => {
      activeRef.current = false
      clearTimeout(restartTimer.current)
      recognitionRef.current?.stop()
      releaseStream()
    }
  }, [releaseStream])

  return { isSupported, start, stop }
}
